import {
  type ComponentType,
  isValidElement,
  type ReactNode,
  useMemo,
  useState,
} from 'react'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import FavoriteIcon from '@mui/icons-material/Favorite'
import InfoIcon from '@mui/icons-material/Info'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import Badge from '@mui/material/Badge'
import Box from '@mui/material/Box'
import Breadcrumbs from '@mui/material/Breadcrumbs'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Collapse from '@mui/material/Collapse'
import Divider from '@mui/material/Divider'
import Fab from '@mui/material/Fab'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormLabel from '@mui/material/FormLabel'
import Grid from '@mui/material/Grid'
import Grow from '@mui/material/Grow'
import Hidden from '@mui/material/Hidden'
import IconButton from '@mui/material/IconButton'
import Input from '@mui/material/Input'
import InputAdornment from '@mui/material/InputAdornment'
import type { InputBaseComponentProps } from '@mui/material/InputBase/InputBase'
import InputLabel from '@mui/material/InputLabel'
import LinearProgress from '@mui/material/LinearProgress'
import Link from '@mui/material/Link'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import MenuList from '@mui/material/MenuList'
import Paper from '@mui/material/Paper'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import MuiSelect from '@mui/material/Select'
import Skeleton from '@mui/material/Skeleton'
import Snackbar from '@mui/material/Snackbar'
import Stack from '@mui/material/Stack'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import MuiStepper from '@mui/material/Stepper'
import { styled } from '@mui/material/styles'
import Switch from '@mui/material/Switch'
import Tab from '@mui/material/Tab'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tabs from '@mui/material/Tabs'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { ContentState, EditorState } from 'draft-js'
import type {
  DocumentFile,
  Field,
  PrimitiveTableColumn,
  StateLabelProps,
} from '@pbt/pbt-ui-components'
import {
  AddButton,
  BackButton,
  ButtonWithLoader,
  Calendar,
  CircularProgressOverlay,
  CityTextField,
  ControlButtonGroup,
  ControlButtonGroupName,
  CopyToClipboard,
  CountryCode,
  CountrySelect,
  CurrencyTextField,
  DebouncedInlineSearch,
  ErrorTooltip,
  FilterLabel,
  HtmlNotesPreview,
  InfiniteLoaderList,
  InlineSearch,
  LinkButton,
  MarketingStylesButton,
  PasswordAdornment,
  PasswordInput,
  PatientAvatar,
  PlusButton,
  PrimitiveTable,
  PuiAutocomplete,
  PuiBaseTooltip,
  PuiCheckbox,
  PuiCloseButton,
  PuiHighlight,
  PuiPopper,
  PuiSelect,
  PuiTextArea,
  PuiTextField,
  PuiTooltip as LegacyPuiTooltip,
  Shadow,
  SignatureView,
  StateLabel,
  StateSelect,
  Text,
  TextInteractive,
  TextWithTooltip,
  TimezoneSelect,
  ZipInput,
} from '@pbt/pbt-ui-components'
import HtmlContentRenderer from '@pbt/pbt-ui-components/src/components/HtmlContentRenderer'

import ActionsButton from '../common/buttons/ActionsButton'
import DownloadButton from '../common/buttons/DownloadButton'
import PuiButtonGroup, {
  type PuiButtonGroupItem,
} from '../common/buttons/PuiButtonGroup'
import QuickFilterButton from '../common/buttons/QuickFilterButton'
import UploadButton from '../common/buttons/UploadButton'
import ColorPinIcon from '../common/icons/ColorPinIcon'
import AgeRangeSelector from '../common/inputs/AgeRangeSelector'
import Attachment from '../common/inputs/attachment/Attachment'
import AttachmentUploadIcon from '../common/inputs/attachment/attachment-dialog/AttachmentUploadIcon'
import AttachmentFileControlButtons from '../common/inputs/attachment/AttachmentFileControlButtons'
import FileInput from '../common/inputs/file-input/FileInput'
import GenderRestrictionSelect from '../common/inputs/gender/GenderRestrictionSelect'
import MenuDropdown from '../common/inputs/MenuDropdown'
import NumericInput from '../common/inputs/NumericInput'
import PercentTextField from '../common/inputs/PercentTextField'
import QuantityInput from '../common/inputs/QuantityInput'
import RequiredFieldsNotice from '../common/inputs/RequiredFieldsNotice'
import RichEdit from '../common/inputs/rich-edit/RichEdit'
import SingleFileSelector from '../common/inputs/SingleFileSelector'
import TaxRateSelect from '../common/inputs/TaxRateSelect'
import TeamMemberSelect from '../common/inputs/TeamMemberSelect'
import TimeSelector from '../common/inputs/time-selector/TimeSelector'
import UserSelect from '../common/inputs/UserSelect'
import WeightRangeInput from '../common/inputs/WeightRangeInput'
import AccentLabel from '../common/labels/AccentLabel'
import AlertLabel from '../common/labels/AlertLabel'
import { PrescriptionStateLabel } from '../common/labels/PrescriptionStateLabel'
import WarningRowLabel from '../common/labels/WarningRowLabel'
import { AlertBanner } from '../elements/AlertBanner/AlertBanner'
import { CollapseArrow } from '../elements/CollapseArrow/CollapseArrow'
import { CollapsibleInformationList } from '../elements/CollapsibleInformationList/CollapsibleInformationList'
import { LabeledSection } from '../elements/LabeledSection/LabeledSection'
import { NumberStepper } from '../elements/NumberStepper/NumberStepper'
import { OutlinedSelect } from '../elements/OutlinedSelect/OutlinedSelect'
import PageAccordion from '../elements/PageAccordion/PageAccordion'
import { Status } from '../elements/Status/Status'
import { Stepper } from '../elements/Stepper/Stepper'
import IconsShowcase from './previews/IconsShowcase'

/* eslint-disable max-lines */
/* eslint-disable react/no-multi-comp */
type AtomicCategory = 'MUI' | 'PBT' | 'RHAP' | 'RHAP/common'
type ComponentFilter = 'form' | 'other'

interface AtomicComponentMeta {
  Example?: ReactNode
  category: AtomicCategory
  count: number
  docsUrl?: string
  filterType?: ComponentFilter
  name: string
}

const noop = () => {}

const FORM_COMPONENT_NAMES = new Set<string>([
  'ButtonWithLoader',
  'IconButton',
  'Button',
  'LinkButton',
  'MarketingStylesButton',
  'AddButton',
  'ActionsButton',
  'QuickFilterButton',
  'UploadButton',
  'PlusButton',
  'PuiButtonGroup',
  'ControlButtonGroup',
  'PuiCloseButton',
  'CopyToClipboard',
  'PuiTextField',
  'PuiSelect',
  'FormControl',
  'FormLabel',
  'FormControlLabel',
  'InputLabel',
  'Input',
  'InputAdornment',
  'Checkbox',
  'PuiCheckbox',
  'Radio',
  'RadioGroup',
  'Switch',
  'Select',
  'MenuItem',
  'MenuList',
  'TextField',
  'Autocomplete',
  'PuiAutocomplete',
  'PuiTextArea',
  'Calendar',
  'StateSelect',
  'CountrySelect',
  'CityTextField',
  'ZipInput',
  'PercentTextField',
  'OutlinedSelect',
  'InlineSearch',
  'DebouncedInlineSearch',
  'FilterLabel',
  'GenderRestrictionSelect',
  'TaxRateSelect',
  'TeamMemberSelect',
  'UserSelect',
  'TimeSelector',
  'AgeRangeSelector',
  'Attachment',
  'AttachmentFileControlButtons',
  'AttachmentUploadIcon',
  'FileInput',
  'WeightRangeInput',
  'NumericInput',
  'QuantityInput',
  'PasswordInput',
  'PasswordAdornment',
  'RichEdit',
  'SingleFileSelector',
  'ErrorTooltip',
  'CurrencyTextField',
  'RequiredFieldsNotice',
  'NumberStepper',
  'TimezoneSelect',
])

const createField = (value: unknown = ''): Field => ({
  errors: [],
  name: 'demo',
  open: false,
  set: noop,
  setValue: noop,
  valid: true,
  value,
})

const buttonWithLoaderPreview = (
  <ButtonWithLoader loading>Loading…</ButtonWithLoader>
)

const puiTextFieldPreview = (
  <PuiTextField fullWidth label="Name" placeholder="Chirp Example" />
)

const iconButtonPreview = (
  <IconButton color="primary">
    <FavoriteIcon />
  </IconButton>
)

const iconsPreview = <IconsShowcase />

const formControlPreview = (
  <FormControl fullWidth variant="standard">
    <InputLabel>Label</InputLabel>
    <Input readOnly value="Sample" />
  </FormControl>
)

const puiSelectPreview = (
  <PuiSelect
    fullWidth
    items={[
      { id: '1', name: 'Option A' },
      { id: '2', name: 'Option B' },
    ]}
    label="Pick one"
    placeholder="Choose"
    value="1"
  />
)

const linkButtonPreview = <LinkButton onClick={() => {}}>Learn more</LinkButton>

const marketingStylesButtonPreview = (
  <MarketingStylesButton size="medium" variant="extended">
    Promote
  </MarketingStylesButton>
)

const dividerPreview = (
  <Box sx={{ width: '100%', p: 2 }}>
    <Typography variant="body2">Content Above</Typography>
    <Divider sx={{ my: 1 }} />
    <Typography variant="body2">Content Below</Typography>
  </Box>
)

const controlButtonGroupPreview = (
  <ControlButtonGroup
    buttons={[
      { name: ControlButtonGroupName.PREVIEW, tooltip: 'Preview' },
      { name: ControlButtonGroupName.EDIT, tooltip: 'Edit' },
      { name: ControlButtonGroupName.DELETE, tooltip: 'Delete' },
    ]}
  />
)

const htmlNotesPreview = (
  <HtmlNotesPreview
    includeLabel
    notes="<p><strong>Instructions:</strong> Give with food.</p>"
  />
)

const currencyTextFieldPreview = (
  <CurrencyTextField label="Budget" value="250.00" />
)

const backButtonPreview = (
  <BackButton label="Back to dashboard" onClick={() => {}} />
)

const accordionPreview = (
  <Accordion defaultExpanded>
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      Accordion Header
    </AccordionSummary>
    <AccordionDetails>
      <div>Accordion body content</div>
    </AccordionDetails>
  </Accordion>
)

const listPreview = (
  <List dense>
    <ListItem>
      <ListItemText primary="First item" secondary="Details" />
    </ListItem>
    <ListItem>
      <ListItemText primary="Second item" secondary="More info" />
    </ListItem>
  </List>
)

const listItemPreview = (
  <List dense>
    <ListItem
      secondaryAction={
        <IconButton edge="end" size="small">
          <InfoIcon fontSize="small" />
        </IconButton>
      }
    >
      <ListItemText primary="Scout" secondary="Checked in · 2:00 PM" />
    </ListItem>
  </List>
)

const listItemTextPreview = (
  <List dense>
    <ListItem>
      <ListItemText
        primary="Dietary note"
        primaryTypographyProps={{ variant: 'subtitle2' }}
        secondary="Grain-free treats only"
      />
    </ListItem>
  </List>
)

const chipPreview = <Chip color="primary" label="Active" variant="outlined" />

const badgePreview = (
  <Badge badgeContent={4} color="secondary">
    <IconButton>
      <FavoriteIcon />
    </IconButton>
  </Badge>
)

const checkboxPreview = <Checkbox checked color="primary" />

const circularProgressPreview = <CircularProgress size={36} />

const fabPreview = (
  <Fab color="secondary" size="medium" variant="circular">
    +
  </Fab>
)

const radioGroupPreview = (
  <FormControl component="fieldset">
    <RadioGroup row value="a">
      <FormControlLabel control={<Radio value="a" />} label="A" />
      <FormControlLabel control={<Radio value="b" />} label="B" />
    </RadioGroup>
  </FormControl>
)

const addButtonPreview = <AddButton addText="Add Item" />

const puiCheckboxPreview = (
  <PuiCheckbox checked label="Accept terms" onChange={() => {}} />
)

const tooltipPreview = (
  <Tooltip title="Helpful hint">
    <Button variant="outlined">Hover me</Button>
  </Tooltip>
)

const selectPreview = (
  <MuiSelect displayEmpty fullWidth value="alpha">
    <MenuItem value="">
      <em>None</em>
    </MenuItem>
    <MenuItem value="alpha">Alpha</MenuItem>
    <MenuItem value="beta">Beta</MenuItem>
  </MuiSelect>
)

const inputAdornmentPreview = (
  <TextField
    fullWidth
    InputProps={{
      startAdornment: <InputAdornment position="start">$</InputAdornment>,
    }}
    label="Amount"
  />
)

const autocompletePreview = (
  <Autocomplete
    disableClearable
    fullWidth
    options={['Apples', 'Bananas', 'Cherries']}
    renderInput={(params) => <TextField {...params} label="Fruit" />}
    value="Bananas"
  />
)

const switchPreview = <Switch checked color="success" />

const alertPreview = (
  <Alert severity="info" variant="outlined">
    Informational alert
  </Alert>
)

const menuListPreview = (
  <MenuList dense>
    <MenuItem>Profile</MenuItem>
    <MenuItem>Settings</MenuItem>
    <MenuItem>Log out</MenuItem>
  </MenuList>
)

const menuItemPreview = (
  <TextField
    fullWidth
    select
    defaultValue="weekly"
    label="Reminder cadence"
    size="small"
  >
    <MenuItem value="daily">Daily digest</MenuItem>
    <MenuItem value="weekly">Weekly rollup</MenuItem>
    <MenuItem value="monthly">Monthly summary</MenuItem>
  </TextField>
)

const UserSelectPreview = () => {
  const [selectedValue, setSelectedValue] = useState<string | undefined>()
  const field: Field = {
    errors: [],
    initialValue: undefined,
    label: 'Assign team member',
    name: 'userSelectDemo',
    open: false,
    set: (next: unknown) => setSelectedValue(next as string | undefined),
    setValue: (next: unknown) => setSelectedValue(next as string | undefined),
    valid: true,
    value: selectedValue,
  }

  return (
    <UserSelect
      displayPetParentOption
      useInMemory
      field={field}
      label="Assign team member"
    />
  )
}

const userSelectPreview = <UserSelectPreview />

const genderRestrictionSelectPreview = (
  <GenderRestrictionSelect
    field={{ ...createField([]), label: 'Allowed genders' }}
  />
)

const taxRateSelectPreview = (
  <TaxRateSelect
    field={{ ...createField([]), label: 'Tax rates' }}
    taxableField={{ ...createField(true), label: 'Taxable' }}
  />
)

const TeamMemberSelectPreview = () => {
  const [value, setValue] = useState<string | undefined>()
  const field: Field = {
    ...createField(value),
    label: 'Assign doctor',
    value,
    set: (next: unknown) => setValue(next as string | undefined),
    setValue: (next: unknown) => setValue(next as string | undefined),
  }

  return (
    <TeamMemberSelect
      field={field}
      label="Primary doctor"
      placeholder="Search team"
    />
  )
}

const teamMemberSelectPreview = <TeamMemberSelectPreview />

const AgeRangeSelectorPreview = () => {
  const [startValue, setStartValue] = useState(2)
  const [endValue, setEndValue] = useState(8)
  const [startUnit, setStartUnit] = useState('YEARS')
  const [endUnit, setEndUnit] = useState('YEARS')

  return (
    <AgeRangeSelector
      range
      endUnit={endUnit}
      endValue={endValue}
      fromLabel="From"
      label="Age range"
      startUnit={startUnit}
      startValue={startValue}
      toLabel="To"
      onEndChange={(value) => setEndValue(value ?? endValue)}
      onEndUnitChange={setEndUnit}
      onStartChange={(value) => setStartValue(value ?? startValue)}
      onStartUnitChange={setStartUnit}
    />
  )
}

const ageRangeSelectorPreview = <AgeRangeSelectorPreview />

const prescriptionStateLabelPreview = (
  <PrescriptionStateLabel
    stateId="rx_preview"
    stateName="Approved"
    variant="small"
  />
)

const RichEditPreview = () => {
  const [editorState, setEditorState] = useState(() =>
    EditorState.createWithContent(
      ContentState.createFromText('Client agreed to treatment plan.'),
    ),
  )

  return (
    <RichEdit
      hidePanel
      editorState={editorState}
      placeholder="Enter visit notes"
      setEditorState={setEditorState}
      showAttachment={false}
      showButtons={false}
      showCheckList={false}
      showDynamicText={false}
    />
  )
}

const richEditPreview = <RichEditPreview />

const weightRangeInputPreview = (
  <Grid container alignItems="center" spacing={1} wrap="nowrap">
    <WeightRangeInput
      field={{ ...createField('LBS'), label: 'Units' }}
      maxWeightField={{ ...createField(60), label: 'Max weight' }}
      minWeightField={{ ...createField(5), label: 'Min weight' }}
    />
  </Grid>
)

const circularProgressOverlayPreview = (
  <div style={{ height: 120, position: 'relative', padding: 16 }}>
    <FormLabel>Settings</FormLabel>
    <FormControlLabel
      control={<Checkbox checked readOnly />}
      label="Enable notifications"
    />
    <FormControlLabel control={<Checkbox />} label="Dark mode" />
    <CircularProgressOverlay open preloaderText="Saving…" />
  </div>
)

const puiTextAreaPreview = (
  <PuiTextArea label="Notes" minRows={3} placeholder="Write something" />
)

const skeletonPreview = (
  <Box sx={{ width: '100%' }}>
    <Skeleton height={40} sx={{ mb: 1 }} variant="text" />
    <Skeleton height={40} sx={{ mb: 1 }} variant="rectangular" />
    <Skeleton height={40} width="60%" />
  </Box>
)

const CollapsePreview = () => {
  const [checked, setChecked] = useState(true)

  return (
    <Box
      sx={{
        height: 120,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        pt: 2,
      }}
    >
      <FormControlLabel
        control={
          <Switch
            checked={checked}
            onChange={() => setChecked((prev) => !prev)}
          />
        }
        label="Show content"
      />
      <Collapse in={checked}>
        <Paper
          elevation={1}
          sx={{ p: 2, mt: 1, width: 200, textAlign: 'center' }}
        >
          Collapsible content
        </Paper>
      </Collapse>
    </Box>
  )
}

const collapsePreview = <CollapsePreview />

const legacyTooltipPreview = (
  <LegacyPuiTooltip tooltipText="Legacy tooltip">
    <IconButton>
      <InfoIcon />
    </IconButton>
  </LegacyPuiTooltip>
)

const buttonPreview = <Button variant="contained">Primary Action</Button>

const linkPreview = (
  <Link href="https://v5.mui.com/material-ui/react-link/" target="_blank">
    MUI Link Docs
  </Link>
)

const paperPreview = (
  <Paper elevation={2} style={{ padding: 12 }}>
    Surface preview
  </Paper>
)

const gridPreview = (
  <Box sx={{ flexGrow: 1 }}>
    <Grid container spacing={2}>
      <Grid item md={8} xs={6}>
        <Paper elevation={1} style={{ padding: 8, textAlign: 'center' }}>
          xs=6 md=8
        </Paper>
      </Grid>
      <Grid item md={4} xs={6}>
        <Paper elevation={1} style={{ padding: 8, textAlign: 'center' }}>
          xs=6 md=4
        </Paper>
      </Grid>
      <Grid item md={4} xs={6}>
        <Paper elevation={1} style={{ padding: 8, textAlign: 'center' }}>
          xs=6 md=4
        </Paper>
      </Grid>
      <Grid item md={8} xs={6}>
        <Paper elevation={1} style={{ padding: 8, textAlign: 'center' }}>
          xs=6 md=8
        </Paper>
      </Grid>
    </Grid>
  </Box>
)

const boxPreview = (
  <Box
    sx={{
      border: '1px dashed rgba(0,0,0,0.25)',
      borderRadius: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5,
      padding: 1.5,
    }}
  >
    <Typography color="text.secondary" variant="caption">
      SOAP summary
    </Typography>
    <Typography variant="body2">
      Review exam findings before finalizing the visit.
    </Typography>
  </Box>
)

const typographyPreview = (
  <Typography align="center" variant="h6">
    Appointment Update
  </Typography>
)

const stackPreview = (
  <Stack alignItems="center" direction="row" spacing={1}>
    <Chip color="primary" label="Scheduled" size="small" />
    <Chip label="Arrived" size="small" variant="outlined" />
    <Chip label="Complete" size="small" />
  </Stack>
)

const hiddenPreview = (
  <Hidden smDown>
    <Paper elevation={1} style={{ padding: 8, textAlign: 'center' }}>
      Visible on md+
    </Paper>
  </Hidden>
)

const LinearProgressPreview = () => (
  <Box sx={{ width: '100%' }}>
    <LinearProgress variant="indeterminate" />
  </Box>
)

const linearProgressPreview = <LinearProgressPreview />

const requiredFieldsNoticePreview = <RequiredFieldsNotice hasRequiredErrors />

const stateLabelPreview = (
  <StateLabel success variant="small">
    Completed
  </StateLabel>
)

const errorTooltipPreview = (
  <ErrorTooltip open message="Please fix errors">
    <span>Field with error</span>
  </ErrorTooltip>
)

const alertLabelPreview = (
  <AlertLabel
    showIcon
    showTooltip
    message="Follow-up needed"
    tooltipMessage="Follow-up needed"
  />
)

const quantityInputPreview = (
  <QuantityInput
    showControls
    availableQuantity={5}
    label="Quantity"
    value={3}
  />
)

const copyToClipboardPreview = <CopyToClipboard isButton text="CHIRP-123" />

const puiCloseButtonPreview = (
  <div style={{ position: 'relative' }}>
    <PuiCloseButton aria-label="Close preview" onClick={() => {}} />
  </div>
)

const timeSelectorPreview = (
  <TimeSelector
    fullWidth
    range
    endValue={new Date(Date.now() + 30 * 60 * 1000).toISOString()}
    label="Appointment window"
    startValue={new Date().toISOString()}
    onEndChange={noop}
    onStartChange={noop}
  />
)

type VisitStatus =
  | 'Scheduled'
  | 'In progress'
  | 'Delayed'
  | 'Completed'
  | 'Cancelled'

type PrimitiveVisitRow = {
  doctor: string
  followUp?: string
  id: string
  owner: string
  patient: string
  phone: string
  status: VisitStatus
  tasks: number
  type: string
  visitTime: string
}

const getStatusLabelProps = (status: VisitStatus): Partial<StateLabelProps> => {
  switch (status) {
    case 'Completed':
      return { success: true }
    case 'In progress':
      return { important: true }
    case 'Delayed':
      return { warning: true }
    case 'Cancelled':
      return { error: true }
    default:
      return {}
  }
}

const renderPatientCell = (visit: PrimitiveVisitRow) => (
  <Stack alignItems="center" direction="row" spacing={1}>
    <PatientAvatar small showBoopBadge={visit.status === 'Completed'} />
    <Stack spacing={0.25}>
      <Text strong variant="body2">
        {visit.patient}
      </Text>
      <Text variant="caption">{visit.visitTime}</Text>
    </Stack>
  </Stack>
)

const renderOwnerCell = (visit: PrimitiveVisitRow) => (
  <Stack spacing={0.25}>
    <Text variant="body2">{visit.owner}</Text>
    <Text variant="caption">{visit.phone}</Text>
  </Stack>
)

const renderTasksCell = (visit: PrimitiveVisitRow) => (
  <TextInteractive color="brand" onClick={noop}>
    {visit.tasks ? `${visit.tasks} open` : 'No open tasks'}
  </TextInteractive>
)

const renderFollowUpCell = (visit: PrimitiveVisitRow) => (
  <Text
    style={{ color: visit.followUp ? '#92400e' : undefined }}
    variant="body3"
  >
    {visit.followUp ?? 'No blockers'}
  </Text>
)

const getVisitRowColor = (status: VisitStatus) => {
  if (status === 'Delayed') {
    return '#fb923c'
  }
  if (status === 'Cancelled') {
    return '#f87171'
  }
  return ''
}

const isVisitDisabled = (visit: PrimitiveVisitRow) =>
  visit.status === 'Cancelled'

const primitiveTableColumns: PrimitiveTableColumn<PrimitiveVisitRow>[] = [
  {
    label: 'Patient',
    width: 3,
    component: renderPatientCell,
  },
  {
    label: 'Owner',
    width: 3,
    component: renderOwnerCell,
  },
  {
    label: 'Visit type',
    width: 2,
    prop: (visit) => visit.type,
  },
  {
    label: 'Status',
    width: 2,
    component: (visit) => (
      <StateLabel variant="small" {...getStatusLabelProps(visit.status)}>
        {visit.status}
      </StateLabel>
    ),
    noTypography: true,
  },
  {
    label: 'Doctor',
    width: 2,
    prop: (visit) => visit.doctor,
  },
  {
    label: 'Visit time',
    width: 2,
    prop: (visit) => visit.visitTime,
  },
  {
    label: 'Tasks',
    width: 1,
    component: renderTasksCell,
    noTypography: true,
  },
  {
    label: 'Follow-up notes',
    width: 3,
    component: renderFollowUpCell,
    hasWarning: (visit) => Boolean(visit.followUp),
  },
]

const primitiveTableRows: PrimitiveVisitRow[] = [
  {
    doctor: 'Dr. Ortiz',
    id: 'visit-001',
    owner: 'Noah Harper',
    patient: 'Luna',
    phone: '(206) 555-9182',
    status: 'Scheduled',
    tasks: 2,
    type: 'Wellness exam',
    visitTime: 'Today · 3:15 PM',
  },
  {
    doctor: 'Dr. Lin',
    followUp: 'Waiting on lab work',
    id: 'visit-002',
    owner: 'Olivia Patel',
    patient: 'Max',
    phone: '(404) 555-1923',
    status: 'In progress',
    tasks: 1,
    type: 'Dental cleaning',
    visitTime: 'Today · 4:00 PM',
  },
  {
    doctor: 'Dr. Silva',
    followUp: 'Client running late',
    id: 'visit-003',
    owner: 'Avery Cohen',
    patient: 'Scout',
    phone: '(415) 555-0197',
    status: 'Delayed',
    tasks: 3,
    type: 'Surgery consult',
    visitTime: 'Today · 4:45 PM',
  },
  {
    doctor: 'Dr. Rivera',
    id: 'visit-004',
    owner: 'Isaiah Brooks',
    patient: 'Mochi',
    phone: '(615) 555-8891',
    status: 'Completed',
    tasks: 0,
    type: 'Telehealth follow-up',
    visitTime: 'Yesterday · 11:30 AM',
  },
  {
    doctor: 'Dr. Ortiz',
    followUp: 'Reschedule requested',
    id: 'visit-005',
    owner: 'Mila James',
    patient: 'Hazel',
    phone: '(312) 555-2021',
    status: 'Cancelled',
    tasks: 0,
    type: 'Vaccination',
    visitTime: 'Tomorrow · 9:00 AM',
  },
]

const primitiveTablePreview = (
  <div style={{ height: 360, flexGrow: 1 }}>
    <PrimitiveTable
      expandable
      columns={primitiveTableColumns}
      filters={{}}
      getDisabled={isVisitDisabled}
      getItemSize={() => 72}
      getRowColoring={(item: PrimitiveVisitRow) =>
        getVisitRowColor(item.status)
      }
      getUniqueId={(item: PrimitiveVisitRow) => item.id}
      isItemLoaded={(index) => index < primitiveTableRows.length}
      isLoading={false}
      itemSpacing={8}
      list={primitiveTableRows}
      loadMoreItems={noop}
      mainColumnCount={2}
      sortings={{}}
      totalCount={primitiveTableRows.length}
    />
  </div>
)

const timezoneGroups = [
  {
    name: 'United States',
    nameTranslation: 'United States',
    timezones: [
      {
        timezone: 'America/New_York',
        name: 'Eastern Time',
        nameTranslation: 'Eastern Time',
      },
      {
        timezone: 'America/Los_Angeles',
        name: 'Pacific Time',
        nameTranslation: 'Pacific Time',
      },
    ],
  },
]

const timezoneSelectPreview = (
  <TimezoneSelect
    useTimezoneGroupVariant
    timezoneGroups={timezoneGroups as any}
    timezones={timezoneGroups[0].timezones as any}
    value="America/New_York"
    onChange={noop}
  />
)

const warningRowLabelPreview = (
  <WarningRowLabel message="Follow up with client" variant="body2" />
)

const numberStepperPreview = <NumberStepper value={2} onChange={noop} />

const downloadButtonPreview = (
  <DownloadButton
    href="https://example.com/invoice.pdf"
    label="Download invoice"
  />
)

const attachmentFile: DocumentFile['file'] = {
  id: 'file-1',
  name: 'lab-results.pdf',
  fileUrl: 'https://example.com/lab-results.pdf',
  required: false,
}

const attachmentFileControlButtonsPreview = (
  <AttachmentFileControlButtons
    file={attachmentFile}
    onDeleteRequested={noop}
    onDownloadRequested={noop}
    onPreviewRequested={noop}
  />
)

const attachmentUploadIconPreview = <AttachmentUploadIcon />

const attachmentPreview = (
  <Attachment
    file={attachmentFile}
    onDownloadRequested={noop}
    onPreviewRequested={noop}
  />
)

const fileInputPreviewImage =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAEklEQVR42mP8/5+hHgAHggJ/PXim+wAAAABJRU5ErkJggg=='

const fileInputPreview = (
  <div style={{ position: 'relative' }}>
    <FileInput
      forceButtonsHidden
      onlyImages
      source={fileInputPreviewImage}
      text="Upload consent photo"
      onFileReady={noop}
      onFileSelected={noop}
    />
  </div>
)

const colorPinIconPreview = (
  <ColorPinIcon color="#fb923c" tooltipText="High priority" />
)

const collapseArrowPreview = <CollapseArrow expanded color="primary" />

const collapsibleInformationListPreview = (
  <CollapsibleInformationList
    initiallyOpened
    list={[
      { id: 'weight', label: 'Weight', value: '42 lbs' },
      { id: 'temp', label: 'Temperature', value: '101 °F' },
    ]}
    title="Vitals"
    titleValue="Today"
  />
)

const labeledSectionPreview = (
  <LabeledSection labelText="Vaccines" variant="info" width="100%">
    <Text variant="body2">Rabies booster scheduled for July 12</Text>
  </LabeledSection>
)

const pageAccordionPreview = (
  <PageAccordion expanded title="Treatment checklist">
    <List dense>
      <ListItem>
        <ListItemText primary="Confirm medications" />
      </ListItem>
      <ListItem>
        <ListItemText primary="Schedule follow-up call" />
      </ListItem>
    </List>
  </PageAccordion>
)

const stepperPreview = (
  <Stepper
    activeStep={1}
    steps={[{ label: 'Intake' }, { label: 'Exam' }, { label: 'Checkout' }]}
  />
)

const calendarPreview = (
  <Calendar
    fullWidth
    showTopPickers={false}
    value="2024-07-15T00:00:00.000Z"
    onChange={noop}
  />
)

const stateSelectPreview = (
  <StateSelect hideLabel field={createField('CA')} onChange={noop} />
)

const countrySelectPreview = (
  <CountrySelect hideLabel field={createField('US')} onChange={noop} />
)

const cityTextFieldPreview = (
  <CityTextField
    country={CountryCode.US}
    label="City"
    value="Seattle"
    onChange={noop}
  />
)

const zipInputPreview = (
  <TextField
    InputProps={{
      inputComponent:
        ZipInput as unknown as ComponentType<InputBaseComponentProps>,
    }}
    label="ZIP code"
    value="19103"
    onChange={noop}
  />
)

const percentTextFieldPreview = (
  <PercentTextField label="Discount" value={12} onChange={noop} />
)

const outlinedSelectId = `outlined-select-${Math.random().toString(36).slice(2, 8)}`

const outlinedSelectPreview = (
  <OutlinedSelect
    getOptionLabel={(option: string) => option}
    id={outlinedSelectId}
    label="Status"
    options={['Scheduled', 'Completed']}
    value="Scheduled"
    onChange={noop}
  />
)

const alertBannerPreview = (
  <AlertBanner
    actions={{ linkText: 'Review', onClick: noop }}
    content="Update invoice template"
    title="Action needed"
    variant="warning"
  />
)

const actionsButtonPreview = (
  <ActionsButton
    actions={[
      { id: 'view', label: 'View details', onClick: noop },
      { id: 'archive', label: 'Archive', redText: true },
    ]}
  />
)

const passwordInputPreview = (
  <PasswordInput adornmentEnabled password="hunter2" onChange={noop} />
)

const passwordAdornmentPreview = (
  <TextField
    InputProps={{ endAdornment: <PasswordAdornment currentLength={6} /> }}
    label="Strength"
    value="Secret"
  />
)

const debouncedInlineSearchPreview = (
  <DebouncedInlineSearch
    initialValue="Next steps"
    placeholder="Search tasks"
    onChange={noop}
  />
)

const menuDropdownPreview = (
  <MenuDropdown
    linkButton
    items={[
      { name: 'Today', value: 'today' },
      { name: 'Tomorrow', value: 'tomorrow' },
      { group: 'Later', items: [{ name: 'Custom' }] },
    ]}
    title="Schedule"
    onSelected={noop}
  />
)

const statusPreview = (
  <Status withAlertIcon variant="warning">
    Requires follow-up
  </Status>
)

const signatureImage =
  'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxODAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCAxODAgNDAiPjxwYXRoIGQ9Ik0xMCwyMCBDMzAsMTAgNTAsMzAgNzAsMjAgUzExMCwxMCAxMzAsMjAgUzE3MCwxMCAxNzAsMzAiIHN0cm9rZT0iIzAwMCIgc3Ryb2tlLXdpZHRoPSIyIiBmaWxsPSJub25lIi8+PC9zdmc+'

const signatureViewPreview = (
  <SignatureView
    signatureDate="May 21, 2024"
    signatureOptions={[
      { id: 'initials', name: 'Use initials', signature: 'AB' },
      { id: 'upload', name: 'Upload new', signature: 'CD' },
    ]}
    signatureString={signatureImage}
    signerName="Dr. Lee"
    onSelect={noop}
  />
)

const htmlContentRendererPreview = (
  <HtmlContentRenderer content="<p><strong>Prep:</strong> Fast 12 hours.</p>" />
)

const puiBaseTooltipPreview = (
  <PuiBaseTooltip tooltipText="Copied link">
    <Button variant="outlined">Hover</Button>
  </PuiBaseTooltip>
)

const quickFilterButtonPreview = (
  <QuickFilterButton active text="Upcoming" onClick={noop} />
)

const uploadButtonPreview = <UploadButton label="Upload file" />

const infiniteLoaderItems = ['Wellness plan', 'Follow-up call', 'Dental exam']

const infiniteLoaderListPreview = (
  <div style={{ height: 180, width: '100%' }}>
    <InfiniteLoaderList
      isItemLoaded={(index) => index < infiniteLoaderItems.length}
      itemCount={infiniteLoaderItems.length}
      itemData={infiniteLoaderItems}
      itemSpacing={8}
      loadMoreItems={noop}
      style={{ height: 160 }}
    >
      {(item) => (
        <Paper elevation={1} style={{ padding: 8 }}>
          {item}
        </Paper>
      )}
    </InfiniteLoaderList>
  </div>
)

const tablePreview = (
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell>Pet</TableCell>
        <TableCell>Owner</TableCell>
        <TableCell align="right">Visits</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      <TableRow>
        <TableCell>Max</TableCell>
        <TableCell>Olivia</TableCell>
        <TableCell align="right">4</TableCell>
      </TableRow>
      <TableRow>
        <TableCell>Luna</TableCell>
        <TableCell>Noah</TableCell>
        <TableCell align="right">2</TableCell>
      </TableRow>
    </TableBody>
  </Table>
)

const formLabelPreview = <FormLabel required>Appointment Type</FormLabel>

const growPreview = (
  <Grow in>
    <Paper elevation={3} style={{ padding: 12 }}>
      Animated surface
    </Paper>
  </Grow>
)

const shadowPreview = (
  <div
    style={{
      position: 'relative',
      borderRadius: 8,
      border: '1px solid #eee',
      padding: 16,
      width: '100%',
      height: 90,
    }}
  >
    Loading inventory…
    <Shadow open />
  </div>
)

const puiAutocompleteField: Field = {
  name: 'species',
  value: [],
  errors: [],
  open: false,
  valid: true,
  set: () => {},
  setValue: () => {},
}

const puiAutocompletePreview = (
  <Box sx={{ width: '100%' }}>
    <PuiAutocomplete
      multiple
      field={puiAutocompleteField}
      label="Species"
      options={[
        { id: 'dog', name: 'Dog' },
        { id: 'cat', name: 'Cat' },
        { id: 'other', name: 'Other' },
      ]}
      translateOptions={false}
    />
  </Box>
)

const filterLabelPreview = (
  <FilterLabel
    filter="status"
    label="Status"
    open={false}
    sublabel="2 active"
    onFilterClick={() => {}}
  />
)

const InlineSearchPreview = () => {
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <InlineSearch
        showCloseButton
        placeholder="Search tasks"
        search={search}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        onChange={(val) => setSearch(val)}
        onClearSearch={() => setSearch('')}
        onClick={() => setOpen(true)}
      />
      {open && (
        <Paper
          elevation={4}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1,
            marginTop: 4,
          }}
        >
          <MenuList>
            <MenuItem onClick={() => setSearch('Wellness')}>Wellness</MenuItem>
            <MenuItem onClick={() => setSearch('Follow-up')}>
              Follow-up
            </MenuItem>
          </MenuList>
        </Paper>
      )}
    </div>
  )
}
const inlineSearchPreview = <InlineSearchPreview />

const plusButtonPreview = <PlusButton label="Add note" onClick={() => {}} />

const puiHighlightPreview = (
  <PuiHighlight searchTerm="refill">Refill authorization required</PuiHighlight>
)

const numericInputPreview = (
  <NumericInput
    allowDecimal
    label="Dosage (mg)"
    value={5}
    onChange={() => {}}
  />
)

const puiButtonItems: PuiButtonGroupItem[] = [
  { name: 'open', label: 'Open' },
  { name: 'closed', label: 'Closed' },
]

const puiButtonGroupPreview = (
  <PuiButtonGroup
    items={puiButtonItems}
    selectedItem="open"
    onItemSelected={() => {}}
  />
)

const patientAvatarPreview = <PatientAvatar showBoopBadge small />

const textWithTooltipPreview = (
  <TextWithTooltip strong tooltipText="DEA 12345" variant="subheading3">
    Dr. Rowan
  </TextWithTooltip>
)

const TextInteractivePreview = () => {
  const [count, setCount] = useState(0)
  const [isLoading, setIsLoading] = useState(false)

  const handleClick = () => {
    setIsLoading(true)
    setTimeout(() => {
      setCount((c) => c + 1)
      setIsLoading(false)
    }, 500)
  }

  return (
    <TextInteractive color="brand" isLoading={isLoading} onClick={handleClick}>
      Click to load and increment: {count}
    </TextInteractive>
  )
}
const textInteractivePreview = <TextInteractivePreview />

const accentLabelPreview = (
  <AccentLabel colorVariant="success" name="PAID IN FULL" variant="small" />
)

const SnackbarPreview = () => {
  const [open, setOpen] = useState(true)
  return (
    <div style={{ position: 'relative', height: 60, width: '100%' }}>
      <Button onClick={() => setOpen(true)}>Show Snackbar</Button>
      <Snackbar
        autoHideDuration={3000}
        message="Client preferences saved"
        open={open}
        style={{ position: 'absolute' }}
        onClose={() => setOpen(false)}
      />
    </div>
  )
}

const snackbarPreview = <SnackbarPreview />
const PuiPopperExample = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        onClick={(event) => setAnchorEl(open ? null : event.currentTarget)}
      >
        Toggle popper
      </Button>
      <PuiPopper
        anchorEl={anchorEl}
        open={open}
        placement="bottom-start"
        onClose={() => setAnchorEl(null)}
      >
        <Paper elevation={3} style={{ padding: 12, width: 180 }}>
          <Text variant="body2">Send reminder?</Text>
          <Button fullWidth color="primary" size="small">
            Schedule
          </Button>
        </Paper>
      </PuiPopper>
    </>
  )
}

const puiPopperPreview = <PuiPopperExample />

const MenuPreview = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  return (
    <>
      <Button onClick={(event) => setAnchorEl(event.currentTarget)}>
        Open menu
      </Button>
      <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
        <MenuItem>Reassign</MenuItem>
        <MenuItem>Complete</MenuItem>
        <MenuItem divider>Archive</MenuItem>
      </Menu>
    </>
  )
}

const menuPreview = <MenuPreview />

const ClickAwayExample = () => {
  const [open, setOpen] = useState(true)
  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <div style={{ position: 'relative' }}>
        <Button
          size="small"
          variant="contained"
          onClick={() => setOpen((value) => !value)}
        >
          {open ? 'Hide' : 'Show'}
        </Button>
        {open && (
          <Paper
            elevation={4}
            style={{ padding: 12, position: 'absolute', top: 40 }}
          >
            <Text variant="body2">Click outside to dismiss</Text>
          </Paper>
        )}
      </div>
    </ClickAwayListener>
  )
}

const clickAwayListenerPreview = <ClickAwayExample />

const MuiStepperPreview = () => (
  <MuiStepper alternativeLabel activeStep={1}>
    {['Request', 'In progress', 'Done'].map((label) => (
      <Step key={label}>
        <StepLabel>{label}</StepLabel>
      </Step>
    ))}
  </MuiStepper>
)

const muiStepperPreview = <MuiStepperPreview />

const breadcrumbsPreview = (
  <Breadcrumbs>
    <Link color="inherit" href="#">
      Tasks
    </Link>
    <Link color="inherit" href="#">
      Wellness plan
    </Link>
    <Text color="textPrimary">Reminder</Text>
  </Breadcrumbs>
)

const TabsPreview = () => {
  const [value, setValue] = useState(0)
  return (
    <Tabs value={value} onChange={(_, next) => setValue(next)}>
      <Tab label="Summary" />
      <Tab label="Notes" />
      <Tab label="History" />
    </Tabs>
  )
}

const tabsPreview = <TabsPreview />

const singleFileSelectorPreview = (
  <SingleFileSelector onSelected={noop}>
    <Button variant="outlined">Select File</Button>
  </SingleFileSelector>
)

const atomicComponentsBase: AtomicComponentMeta[] = [
  {
    count: 1128,
    name: 'Grid',
    category: 'MUI',
    docsUrl: 'https://v5.mui.com/material-ui/react-grid/',
    Example: gridPreview,
  },
  {
    count: 291,
    name: 'Icons',
    category: 'MUI',
    docsUrl: 'https://v5.mui.com/material-ui/material-icons/',
    Example: iconsPreview,
  },
  {
    count: 241,
    name: 'ButtonWithLoader',
    category: 'PBT',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-ui-components/blob/master/src/components/buttons/ButtonWithLoader.tsx',
    Example: buttonWithLoaderPreview,
  },
  {
    count: 182,
    name: 'PuiTextField',
    category: 'PBT',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-ui-components/blob/master/src/components/inputs/PuiTextField.tsx',
    Example: puiTextFieldPreview,
  },
  {
    count: 160,
    name: 'Box',
    category: 'MUI',
    docsUrl: 'https://v5.mui.com/system/box/',
    Example: boxPreview,
  },
  {
    count: 123,
    name: 'IconButton',
    category: 'MUI',
    docsUrl: 'https://v5.mui.com/material-ui/react-button/#icon-button',
    Example: iconButtonPreview,
  },
  {
    count: 116,
    name: 'PuiSelect',
    category: 'PBT',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-ui-components/blob/master/src/components/inputs/PuiSelect.tsx',
    Example: puiSelectPreview,
  },
  {
    count: 100,
    name: 'TextWithTooltip',
    category: 'PBT',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-ui-components/blob/master/src/components/TextWithTooltip.tsx',
    Example: textWithTooltipPreview,
  },
  {
    count: 94,
    name: 'FormControl',
    category: 'MUI',
    docsUrl: 'https://v5.mui.com/material-ui/react-text-field/',
    Example: formControlPreview,
  },
  {
    count: 87,
    name: 'CircularProgress',
    category: 'MUI',
    docsUrl: 'https://v5.mui.com/material-ui/react-progress/',
    Example: circularProgressPreview,
  },
  {
    count: 85,
    name: 'Fab',
    category: 'MUI',
    docsUrl: 'https://v5.mui.com/material-ui/react-floating-action-button/',
    Example: fabPreview,
  },
  {
    count: 81,
    name: 'InputLabel',
    category: 'MUI',
    docsUrl: 'https://v5.mui.com/material-ui/api/input-label/',
    Example: formControlPreview,
  },
  {
    count: 74,
    name: 'Input',
    category: 'MUI',
    docsUrl: 'https://v5.mui.com/material-ui/api/input/',
    Example: formControlPreview,
  },
  {
    count: 68,
    name: 'FormControlLabel',
    category: 'MUI',
    docsUrl: 'https://v5.mui.com/material-ui/api/form-control-label/',
    Example: radioGroupPreview,
  },
  {
    count: 66,
    name: 'Radio',
    category: 'MUI',
    docsUrl: 'https://v5.mui.com/material-ui/react-radio-button/',
    Example: radioGroupPreview,
  },
  {
    count: 60,
    name: 'AddButton',
    category: 'PBT',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-ui-components/blob/master/src/components/buttons/AddButton.tsx',
    Example: addButtonPreview,
  },
  {
    count: 60,
    name: 'TextInteractive',
    category: 'PBT',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-ui-components/blob/master/src/components/TextInteractive.tsx',
    Example: textInteractivePreview,
  },
  {
    count: 59,
    name: 'PuiCheckbox',
    category: 'PBT',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-ui-components/blob/master/src/components/inputs/PuiCheckbox.tsx',
    Example: puiCheckboxPreview,
  },
  {
    count: 59,
    name: 'Table',
    category: 'MUI',
    docsUrl: 'https://v5.mui.com/material-ui/react-table/',
    Example: tablePreview,
  },
  {
    count: 57,
    name: 'Tooltip',
    category: 'MUI',
    docsUrl: 'https://v5.mui.com/material-ui/react-tooltip/',
    Example: tooltipPreview,
  },
  {
    count: 54,
    name: 'RadioGroup',
    category: 'MUI',
    docsUrl: 'https://v5.mui.com/material-ui/api/radio-group/',
    Example: radioGroupPreview,
  },
  {
    count: 53,
    name: 'StateLabel',
    category: 'PBT',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-ui-components/blob/master/src/components/labels/StateLabel.tsx',
    Example: stateLabelPreview,
  },
  {
    count: 52,
    name: 'CircularProgressOverlay',
    category: 'PBT',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-ui-components/blob/master/src/components/CircularProgressOverlay.tsx',
    Example: circularProgressOverlayPreview,
  },
  {
    count: 52,
    name: 'Stack',
    category: 'MUI',
    docsUrl: 'https://v5.mui.com/material-ui/react-stack/',
    Example: stackPreview,
  },
  {
    count: 50,
    name: 'ErrorTooltip',
    category: 'PBT',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-ui-components/blob/master/src/components/ErrorTooltip.tsx',
    Example: errorTooltipPreview,
  },
  {
    count: 50,
    name: 'PuiTextArea',
    category: 'PBT',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-ui-components/blob/master/src/components/inputs/PuiTextArea.tsx',
    Example: puiTextAreaPreview,
  },
  {
    count: 50,
    name: 'Skeleton',
    category: 'MUI',
    docsUrl: 'https://v5.mui.com/material-ui/react-skeleton/',
    Example: skeletonPreview,
  },
  {
    count: 39,
    name: 'Calendar',
    category: 'PBT',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-ui-components/blob/master/src/components/inputs/calendar/Calendar.tsx',
    Example: calendarPreview,
  },
  {
    count: 39,
    name: 'Collapse',
    category: 'MUI',
    docsUrl: 'https://v5.mui.com/material-ui/react-collapse/',
    Example: collapsePreview,
  },
  {
    count: 39,
    name: 'PuiTooltip',
    category: 'PBT',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-ui-components/blob/master/src/components/PuiTooltip.tsx',
    Example: legacyTooltipPreview,
  },
  {
    count: 39,
    name: 'UserSelect',
    category: 'RHAP/common',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-portal-ui/blob/master/src/components/common/inputs/UserSelect.tsx',
    Example: userSelectPreview,
  },
  {
    count: 38,
    name: 'Button',
    category: 'MUI',
    Example: buttonPreview,
    docsUrl: 'https://v5.mui.com/material-ui/react-button/',
  },
  {
    count: 38,
    name: 'Link',
    category: 'MUI',
    Example: linkPreview,
    docsUrl: 'https://v5.mui.com/material-ui/react-link/',
  },
  {
    count: 37,
    name: 'LinkButton',
    category: 'PBT',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-ui-components/blob/master/src/components/buttons/LinkButton.tsx',
    Example: linkButtonPreview,
  },
  {
    count: 36,
    name: 'Divider',
    category: 'MUI',
    Example: dividerPreview,
    docsUrl: 'https://v5.mui.com/material-ui/react-divider/',
  },
  {
    count: 33,
    name: 'ControlButtonGroup',
    category: 'PBT',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-ui-components/blob/master/src/components/buttons/ControlButtonGroup.tsx',
    Example: controlButtonGroupPreview,
  },
  {
    count: 33,
    name: 'HtmlNotesPreview',
    category: 'PBT',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-ui-components/blob/master/src/components/html-preview/HtmlNotesPreview.tsx',
    Example: htmlNotesPreview,
  },
  {
    count: 30,
    name: 'CurrencyTextField',
    category: 'PBT',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-ui-components/blob/master/src/components/inputs/CurrencyTextField.tsx',
    Example: currencyTextFieldPreview,
  },
  {
    count: 30,
    name: 'RequiredFieldsNotice',
    category: 'RHAP/common',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-portal-ui/blob/master/src/components/common/inputs/RequiredFieldsNotice.tsx',
    Example: requiredFieldsNoticePreview,
  },
  {
    count: 30,
    name: 'Hidden',
    category: 'MUI',
    docsUrl: 'https://v5.mui.com/material-ui/react-hidden/',
    Example: hiddenPreview,
  },
  {
    count: 30,
    name: 'MenuItem',
    category: 'MUI',
    Example: menuItemPreview,
  },
  {
    count: 29,
    name: 'PuiPopper',
    category: 'PBT',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-ui-components/blob/master/src/components/PuiPopper.tsx',
    Example: puiPopperPreview,
  },
  {
    count: 28,
    name: 'Checkbox',
    category: 'MUI',
    Example: checkboxPreview,
    docsUrl: 'https://v5.mui.com/material-ui/react-checkbox/',
  },
  {
    count: 27,
    name: 'BackButton',
    category: 'PBT',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-ui-components/blob/master/src/components/BackButton.tsx',
    Example: backButtonPreview,
  },
  {
    count: 25,
    name: 'Paper',
    category: 'MUI',
    Example: paperPreview,
    docsUrl: 'https://v5.mui.com/material-ui/react-paper/',
  },
  {
    count: 24,
    name: 'AlertLabel',
    category: 'RHAP/common',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-portal-ui/blob/master/src/components/common/labels/AlertLabel.tsx',
    Example: alertLabelPreview,
  },
  {
    count: 23,
    name: 'QuantityInput',
    category: 'RHAP/common',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-portal-ui/blob/master/src/components/common/inputs/QuantityInput.tsx',
    Example: quantityInputPreview,
  },
  {
    count: 21,
    name: 'InputAdornment',
    category: 'MUI',
    docsUrl: 'https://v5.mui.com/material-ui/api/input-adornment/',
    Example: inputAdornmentPreview,
  },
  {
    count: 21,
    name: 'InfiniteLoaderList',
    category: 'PBT',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-ui-components/blob/master/src/components/lists/InfiniteLoaderList.tsx',
    Example: infiniteLoaderListPreview,
  },
  {
    count: 20,
    name: 'NumericInput',
    category: 'RHAP/common',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-portal-ui/blob/master/src/components/common/inputs/NumericInput.tsx',
    Example: numericInputPreview,
  },
  {
    count: 18,
    name: 'Status',
    category: 'RHAP',
    Example: statusPreview,
    docsUrl:
      'https://portal.stage.rhapsody.vet/storybook/?path=/docs/design-systems-status--docs',
  },
  {
    count: 18,
    name: 'AccentLabel',
    category: 'RHAP/common',
    Example: accentLabelPreview,
  },
  {
    count: 15,
    name: 'ClickAwayListener',
    category: 'MUI',
    docsUrl: 'https://v5.mui.com/material-ui/react-click-away-listener/',
    Example: clickAwayListenerPreview,
  },
  {
    count: 15,
    name: 'PatientAvatar',
    category: 'PBT',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-ui-components/blob/master/src/components/PatientAvatar.tsx',
    Example: patientAvatarPreview,
  },
  {
    count: 14,
    name: 'MenuDropdown',
    category: 'RHAP/common',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-portal-ui/blob/master/src/components/common/inputs/MenuDropdown.tsx',
    Example: menuDropdownPreview,
  },
  {
    count: 14,
    name: 'Tabs',
    category: 'MUI',
    Example: tabsPreview,
  },
  {
    count: 11,
    name: 'Select',
    category: 'MUI',
    Example: selectPreview,
    docsUrl: 'https://v5.mui.com/material-ui/react-select/',
  },
  {
    count: 12,
    name: 'Snackbar',
    category: 'MUI',
    Example: snackbarPreview,
  },
  {
    count: 10,
    name: 'AlertBanner',
    category: 'RHAP',
    docsUrl:
      'https://portal.stage.rhapsody.vet/storybook/?path=/docs/design-systems-alertbanner--docs',
    Example: alertBannerPreview,
  },
  {
    count: 10,
    name: 'FileInput',
    category: 'RHAP/common',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-portal-ui/blob/master/src/components/common/inputs/file-input/FileInput.tsx',
    Example: fileInputPreview,
  },
  {
    count: 10,
    name: 'LinearProgress',
    category: 'MUI',
    docsUrl: 'https://v5.mui.com/material-ui/react-progress/',
    Example: linearProgressPreview,
  },
  {
    count: 10,
    name: 'MarketingStylesButton',
    category: 'PBT',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-ui-components/blob/master/src/components/buttons/MarketingStylesButton.tsx',
    Example: marketingStylesButtonPreview,
  },
  {
    count: 10,
    name: 'StateSelect',
    category: 'PBT',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-ui-components/blob/master/src/components/inputs/StateSelect.tsx',
    Example: stateSelectPreview,
  },
  {
    count: 10,
    name: 'ZipInput',
    category: 'PBT',
    Example: zipInputPreview,
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-ui-components/blob/master/src/components/form-inputs/ZipInput.tsx',
  },
  {
    count: 9,
    name: 'Accordion',
    category: 'MUI',
    Example: accordionPreview,
    docsUrl: 'https://v5.mui.com/material-ui/react-accordion/',
  },
  {
    count: 9,
    name: 'Breadcrumbs',
    category: 'MUI',
    Example: breadcrumbsPreview,
  },
  {
    count: 9,
    name: 'ActionsButton',
    category: 'RHAP/common',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-portal-ui/blob/master/src/components/common/buttons/ActionsButton.tsx',
    Example: actionsButtonPreview,
  },
  {
    count: 9,
    name: 'CopyToClipboard',
    category: 'PBT',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-ui-components/blob/master/src/components/CopyToClipboard.tsx',
    Example: copyToClipboardPreview,
  },
  {
    count: 9,
    name: 'CountrySelect',
    category: 'PBT',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-ui-components/blob/master/src/components/inputs/CountrySelect.tsx',
    Example: countrySelectPreview,
  },
  {
    count: 9,
    name: 'PuiButtonGroup',
    category: 'RHAP/common',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-portal-ui/blob/master/src/components/common/buttons/PuiButtonGroup.tsx',
    Example: puiButtonGroupPreview,
  },
  {
    count: 9,
    name: 'Shadow',
    category: 'PBT',
    Example: shadowPreview,
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-ui-components/blob/master/src/components/Shadow.tsx',
  },
  {
    count: 9,
    name: 'TimeSelector',
    category: 'RHAP/common',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-portal-ui/blob/master/src/components/common/inputs/time-selector/TimeSelector.tsx',
    Example: timeSelectorPreview,
  },
  {
    count: 8,
    name: 'CityTextField',
    category: 'PBT',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-ui-components/blob/master/src/components/inputs/CityTextField.tsx',
    Example: cityTextFieldPreview,
  },
  {
    count: 8,
    name: 'Menu',
    category: 'MUI',
    Example: menuPreview,
    docsUrl: 'https://v5.mui.com/material-ui/react-menu/',
  },
  {
    count: 8,
    name: 'OutlinedSelect',
    category: 'RHAP',
    docsUrl:
      'https://portal.stage.rhapsody.vet/storybook/?path=/docs/design-systems-outlinedselect--docs',
    Example: outlinedSelectPreview,
  },
  {
    count: 8,
    name: 'PercentTextField',
    category: 'RHAP/common',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-portal-ui/blob/master/src/components/common/inputs/PercentTextField.tsx',
    Example: percentTextFieldPreview,
  },
  {
    count: 8,
    name: 'PuiCloseButton',
    category: 'PBT',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-ui-components/blob/master/src/components/buttons/PuiCloseButton.tsx',
    Example: puiCloseButtonPreview,
  },
  {
    count: 7,
    name: 'List',
    category: 'MUI',
    docsUrl: 'https://v5.mui.com/material-ui/react-list/',
    Example: listPreview,
  },
  {
    count: 7,
    name: 'ListItemText',
    category: 'MUI',
    Example: listItemTextPreview,
  },
  {
    count: 7,
    name: 'PrimitiveTable',
    category: 'PBT',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-ui-components/blob/master/src/components/lists/primitive-table/PrimitiveTable.tsx',
    Example: primitiveTablePreview,
  },
  {
    count: 7,
    name: 'SignatureView',
    category: 'PBT',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-ui-components/blob/master/src/components/signature/SignatureView.tsx',
    Example: signatureViewPreview,
  },
  {
    count: 6,
    name: 'Alert',
    category: 'MUI',
    Example: alertPreview,
    docsUrl: 'https://v5.mui.com/material-ui/react-alert/',
  },
  {
    count: 6,
    name: 'Attachment',
    category: 'RHAP/common',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-portal-ui/blob/master/src/components/common/inputs/attachment/Attachment.tsx',
    Example: attachmentPreview,
  },
  {
    count: 6,
    name: 'Autocomplete',
    category: 'MUI',
    docsUrl: 'https://v5.mui.com/material-ui/react-autocomplete/',
    Example: autocompletePreview,
  },
  {
    count: 6,
    name: 'DebouncedInlineSearch',
    category: 'PBT',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-ui-components/blob/master/src/components/search/DebouncedInlineSearch.tsx',
    Example: debouncedInlineSearchPreview,
  },
  {
    count: 6,
    name: 'FormLabel',
    category: 'MUI',
    Example: formLabelPreview,
    docsUrl: 'https://v5.mui.com/material-ui/api/form-label/',
  },
  {
    count: 6,
    name: 'GenderRestrictionSelect',
    category: 'RHAP/common',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-portal-ui/blob/master/src/components/common/inputs/gender/GenderRestrictionSelect.tsx',
    Example: genderRestrictionSelectPreview,
  },
  {
    count: 6,
    name: 'MenuList',
    category: 'MUI',
    Example: menuListPreview,
    docsUrl: 'https://v5.mui.com/material-ui/react-menu/',
  },
  {
    count: 6,
    name: 'PasswordInput',
    category: 'PBT',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-ui-components/blob/master/src/components/inputs/PasswordInput.tsx',
    Example: passwordInputPreview,
  },
  {
    count: 6,
    name: 'QuickFilterButton',
    category: 'RHAP/common',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-portal-ui/blob/master/src/components/common/buttons/QuickFilterButton.tsx',
    Example: quickFilterButtonPreview,
  },
  {
    count: 6,
    name: 'TaxRateSelect',
    category: 'RHAP/common',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-portal-ui/blob/master/src/components/common/inputs/TaxRateSelect.tsx',
    Example: taxRateSelectPreview,
  },
  {
    count: 6,
    name: 'TeamMemberSelect',
    category: 'RHAP/common',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-portal-ui/blob/master/src/components/common/inputs/TeamMemberSelect.tsx',
    Example: teamMemberSelectPreview,
  },
  {
    count: 6,
    name: 'UploadButton',
    category: 'RHAP/common',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-portal-ui/blob/master/src/components/common/buttons/UploadButton.tsx',
    Example: uploadButtonPreview,
  },
  {
    count: 6,
    name: 'WarningRowLabel',
    category: 'RHAP/common',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-portal-ui/blob/master/src/components/common/labels/WarningRowLabel.tsx',
    Example: warningRowLabelPreview,
  },
  {
    count: 5,
    name: 'AgeRangeSelector',
    category: 'RHAP/common',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-portal-ui/blob/master/src/components/common/inputs/AgeRangeSelector.tsx',
    Example: ageRangeSelectorPreview,
  },
  {
    count: 5,
    name: 'AttachmentFileControlButtons',
    category: 'RHAP/common',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-portal-ui/blob/master/src/components/common/inputs/attachment/AttachmentFileControlButtons.tsx',
    Example: attachmentFileControlButtonsPreview,
  },
  {
    count: 5,
    name: 'AttachmentUploadIcon',
    category: 'RHAP/common',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-portal-ui/blob/master/src/components/common/inputs/attachment/attachment-dialog/AttachmentUploadIcon.tsx',
    Example: attachmentUploadIconPreview,
  },
  {
    count: 5,
    name: 'ColorPinIcon',
    category: 'RHAP/common',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-portal-ui/blob/master/src/components/common/icons/ColorPinIcon.tsx',
    Example: colorPinIconPreview,
  },
  {
    count: 5,
    name: 'DownloadButton',
    category: 'RHAP/common',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-portal-ui/blob/master/src/components/common/buttons/DownloadButton.tsx',
    Example: downloadButtonPreview,
  },
  {
    count: 5,
    name: 'Grow',
    category: 'MUI',
    Example: growPreview,
    docsUrl: 'https://v5.mui.com/material-ui/transitions/#grow',
  },
  {
    count: 5,
    name: 'ListItem',
    category: 'MUI',
    Example: listItemPreview,
  },
  {
    count: 5,
    name: 'PrescriptionStateLabel',
    category: 'RHAP/common',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-portal-ui/blob/master/src/components/common/labels/PrescriptionStateLabel.tsx',
    Example: prescriptionStateLabelPreview,
  },
  {
    count: 5,
    name: 'PuiAutocomplete',
    category: 'PBT',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-ui-components/blob/master/src/components/inputs/PuiAutocomplete.tsx',
    Example: puiAutocompletePreview,
  },
  {
    count: 5,
    name: 'RichEdit',
    category: 'RHAP/common',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-portal-ui/blob/master/src/components/common/inputs/rich-edit/RichEdit.tsx',
    Example: richEditPreview,
  },
  {
    count: 5,
    name: 'SingleFileSelector',
    category: 'RHAP/common',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-portal-ui/blob/master/src/components/common/inputs/SingleFileSelector.tsx',
    Example: singleFileSelectorPreview,
  },
  {
    count: 5,
    name: 'WeightRangeInput',
    category: 'RHAP/common',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-portal-ui/blob/master/src/components/common/inputs/WeightRangeInput.tsx',
    Example: weightRangeInputPreview,
  },
  {
    count: 4,
    name: 'Badge',
    category: 'MUI',
    Example: badgePreview,
    docsUrl: 'https://v5.mui.com/material-ui/react-badge/',
  },
  {
    count: 4,
    name: 'FilterLabel',
    category: 'PBT',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-ui-components/blob/master/src/components/lists/primitive-table/filters/FilterLabel.tsx',
    Example: filterLabelPreview,
  },
  {
    count: 4,
    name: 'InlineSearch',
    category: 'PBT',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-ui-components/blob/master/src/components/search/InlineSearch.tsx',
    Example: inlineSearchPreview,
  },
  {
    count: 4,
    name: 'NumberStepper',
    category: 'RHAP',
    docsUrl:
      'https://portal.stage.rhapsody.vet/storybook/?path=/docs/design-systems-numberstepper--docs',
    Example: numberStepperPreview,
  },
  {
    count: 4,
    name: 'PasswordAdornment',
    category: 'PBT',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-ui-components/blob/master/src/components/inputs/PasswordAdornment.tsx',
    Example: passwordAdornmentPreview,
  },
  {
    count: 4,
    name: 'PlusButton',
    category: 'PBT',
    Example: plusButtonPreview,
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-ui-components/blob/master/src/components/buttons/PlusButton.tsx',
  },
  {
    count: 4,
    name: 'PuiHighlight',
    category: 'PBT',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-ui-components/blob/master/src/components/PuiHighlight.tsx',
    Example: puiHighlightPreview,
  },
  {
    count: 4,
    name: 'TimezoneSelect',
    category: 'PBT',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-ui-components/blob/master/src/components/inputs/TimezoneSelect.tsx',
    Example: timezoneSelectPreview,
  },
  {
    count: 3,
    name: 'Chip',
    category: 'MUI',
    Example: chipPreview,
    docsUrl: 'https://v5.mui.com/material-ui/react-chip/',
  },
  {
    count: 3,
    name: 'Typography',
    category: 'MUI',
    docsUrl: 'https://v5.mui.com/material-ui/react-typography/',
    Example: typographyPreview,
  },
  {
    count: 3,
    name: 'CollapseArrow',
    category: 'RHAP',
    docsUrl:
      'https://portal.stage.rhapsody.vet/storybook/?path=/docs/design-systems-collapsearrow--docs',
    Example: collapseArrowPreview,
  },
  {
    count: 3,
    name: 'CollapsibleInformationList',
    category: 'RHAP',
    docsUrl:
      'https://portal.stage.rhapsody.vet/storybook/?path=/docs/design-systems-collapsibleinformationlist--docs',
    Example: collapsibleInformationListPreview,
  },
  {
    count: 3,
    name: 'HtmlContentRenderer',
    category: 'PBT',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-ui-components/blob/master/src/components/HtmlContentRenderer.tsx',
    Example: htmlContentRendererPreview,
  },
  {
    count: 3,
    name: 'PuiBaseTooltip',
    category: 'PBT',
    docsUrl:
      'https://github.com/Chewy-Inc/pbt-ui-components/blob/master/src/components/PuiBaseTooltip.tsx',
    Example: puiBaseTooltipPreview,
  },
  {
    count: 3,
    name: 'Switch',
    category: 'MUI',
    Example: switchPreview,
    docsUrl: 'https://v5.mui.com/material-ui/react-switch/',
  },
  {
    count: 2,
    name: 'MuiStepper',
    category: 'MUI',
    Example: muiStepperPreview,
    docsUrl: 'https://v5.mui.com/material-ui/react-stepper/',
  },
  {
    count: 1,
    name: 'LabeledSection',
    category: 'RHAP',
    docsUrl:
      'https://portal.stage.rhapsody.vet/storybook/?path=/docs/design-systems-labeledsection--docs',
    Example: labeledSectionPreview,
  },
  {
    count: 1,
    name: 'PageAccordion',
    category: 'RHAP',
    docsUrl:
      'https://portal.stage.rhapsody.vet/storybook/?path=/docs/design-systems-pageaccordion--docs',
    Example: pageAccordionPreview,
  },
  {
    count: 1,
    name: 'Stepper',
    category: 'RHAP',
    docsUrl:
      'https://portal.stage.rhapsody.vet/storybook/?path=/docs/design-systems-stepper--docs',
    Example: stepperPreview,
  },
]

const atomicComponents: AtomicComponentMeta[] = atomicComponentsBase.map(
  (component) => ({
    ...component,
    filterType:
      component.filterType ??
      (FORM_COMPONENT_NAMES.has(component.name) ? 'form' : 'other'),
  }),
)

const Root = styled('div')(({ theme }) => ({
  padding: theme.spacing(4),
  width: '100%',
}))

const HeaderRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
}))

const Header = styled('h1')(({ theme }) => ({
  fontSize: 24,
  fontWeight: 600,
  margin: 0,
  textAlign: 'right',
  color: theme.palette.text.primary,
}))

const GridContainer = styled('div')(({ theme }) => ({
  width: '100%',
  display: 'grid',
  gap: theme.spacing(2),
  gridTemplateColumns: 'repeat(auto-fit, minmax(640px, 1fr))',
}))

const Block = styled('div')(({ theme }) => ({
  border: `1px dashed ${theme.palette.divider}`,
  borderRadius: 8,
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
}))

const BlockHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(1),
  fontWeight: 600,
}))

const Meta = styled('span')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}))

const CountBadge = styled('span')(({ theme }) => ({
  borderRadius: 4,
  padding: theme.spacing(0.25, 1),
  fontSize: 12,
  backgroundColor: theme.palette.grey[100],
  color: theme.palette.text.secondary,
}))

const CategoryBadge = styled('span')(({ theme }) => ({
  borderRadius: 4,
  padding: theme.spacing(0.25, 1),
  fontSize: 12,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  backgroundColor: theme.palette.grey[200],
  color: theme.palette.grey[800],
  display: 'inline-block',
}))

const CategoryBadgeLink = styled('a')(({ theme }) => ({
  borderRadius: 4,
  padding: theme.spacing(0.25, 1),
  fontSize: 12,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  backgroundColor: theme.palette.grey[200],
  color: theme.palette.grey[800],
  textDecoration: 'none',
  display: 'inline-block',
  '&:hover': {
    textDecoration: 'underline',
    cursor: 'pointer',
  },
}))

const Preview = styled('div')(({ theme }) => ({
  minHeight: 120,
  borderRadius: 4,
  border: `1px solid ${theme.palette.grey[300]}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.grey[500],
  fontSize: 14,
}))

const Placeholder = styled('span')(({ theme }) => ({
  color: theme.palette.grey[500],
}))

const FilterTabs = styled(Tabs)(({ theme }) => ({
  minHeight: 36,
  marginRight: 'auto',
  '& .MuiTabs-flexContainer': {
    gap: theme.spacing(1),
  },
  '& .MuiTab-root': {
    minHeight: 32,
    textTransform: 'none',
    padding: theme.spacing(0.5, 1.5),
  },
}))

const totalComponentCount = atomicComponents.length
const formComponentCount = atomicComponents.filter(
  (component) => component.filterType === 'form',
).length
const otherComponentCount = totalComponentCount - formComponentCount

type ComponentFilterValue = 'all' | ComponentFilter

const AtomicComponentsPage = () => {
  const [activeFilter, setActiveFilter] = useState<ComponentFilterValue>('all')

  const filteredComponents = useMemo(() => {
    if (activeFilter === 'all') {
      return atomicComponents
    }
    return atomicComponents.filter(
      (component) => component.filterType === activeFilter,
    )
  }, [activeFilter])

  return (
    <Root>
      <HeaderRow>
        <FilterTabs
          aria-label="Component filter tabs"
          textColor="primary"
          value={activeFilter}
          onChange={(_, value: ComponentFilterValue) => setActiveFilter(value)}
        >
          <Tab label={`All (${totalComponentCount})`} value="all" />
          <Tab label={`Form (${formComponentCount})`} value="form" />
          <Tab label={`Other (${otherComponentCount})`} value="other" />
        </FilterTabs>
        <Header>Chirp Atomic Components Sandbox</Header>
      </HeaderRow>
      <GridContainer>
        {filteredComponents.map((component) => {
          const preview = component.Example
          const content =
            preview !== undefined ? (
              isValidElement(preview) ? (
                preview
              ) : (
                <>{preview}</>
              )
            ) : (
              <Placeholder>Placeholder for {component.name}</Placeholder>
            )

          return (
            <Block key={component.name}>
              <BlockHeader>
                <span>{component.name}</span>
                <Meta>
                  <CountBadge>{component.count} uses</CountBadge>
                  {component.docsUrl ? (
                    <CategoryBadgeLink
                      href={component.docsUrl}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {component.category}
                    </CategoryBadgeLink>
                  ) : (
                    <CategoryBadge>{component.category}</CategoryBadge>
                  )}
                </Meta>
              </BlockHeader>
              <Preview>{content}</Preview>
            </Block>
          )
        })}
      </GridContainer>
    </Root>
  )
}

export default AtomicComponentsPage
